import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useVaults } from '../hooks/useVaults';
import { useChatHistory } from '../hooks/useChatHistory';

export interface VaultBot {
  id: string;
  name: string;
  avatar: string;
  role: string;
  description: string;
  system_prompt: string;
  vault_id?: string | null;
  created_at: string;
}

const PRESET_BOTS: VaultBot[] = [
  {
    id: 'bot_legal',
    name: 'Luật Sư & Cố Vấn Hợp Đồng',
    avatar: '⚖️',
    role: 'Legal Auditor',
    description: 'Chuyên gia rà soát hợp đồng, điều khoản cam kết, cảnh báo rủi ro pháp lý và tranh chấp.',
    system_prompt: 'Bạn là Cố vấn Pháp lý Cấp cao. Khi phân tích văn bản, hãy chú trọng tính pháp lý, quyền và nghĩa vụ các bên, điều khoản bồi thường, bảo mật và các bẫy rủi ro tiềm ẩn.',
    created_at: new Date().toISOString()
  },
  {
    id: 'bot_finance',
    name: 'Chuyên Gia Phân Tích Tài Chính',
    avatar: '📊',
    role: 'Financial Analyst',
    description: 'Bóc tách báo cáo tài chính, dòng tiền, dự phóng ngân sách và kiểm tra số liệu bảng tính.',
    system_prompt: 'Bạn là Giám đốc Tài chính (CFO) kỳ cựu. Hãy phân tích chuyên sâu các chỉ số tài chính, biên lợi nhuận, cấu trúc chi phí, cảnh báo thâm hụt và đề xuất tối ưu dòng tiền.',
    created_at: new Date().toISOString()
  },
  {
    id: 'bot_tech',
    name: 'Kiến Trúc Sư Hệ Thống & Bảo Mật',
    avatar: '🛡️',
    role: 'Tech Architect',
    description: 'Thẩm định kiến trúc kỹ thuật, thiết kế microservices, schema database và an toàn thông tin.',
    system_prompt: 'Bạn là Principal Solution Architect. Hãy đánh giá hệ thống theo tiêu chuẩn Zero-Trust, khả năng chịu tải cao (high scalability), độ trễ thấp và khả năng bảo mật cấp quân sự.',
    created_at: new Date().toISOString()
  },
  {
    id: 'bot_coach',
    name: 'Cố Vấn Lãnh Đạo & Phản Biện',
    avatar: '🦉',
    role: 'Executive Coach',
    description: 'Đóng vai trò Devil\'s Advocate để chất vấn kế hoạch, chỉ ra điểm mù và rèn luyện tư duy phản biện.',
    system_prompt: 'Bạn là Executive Coach và cố vấn chiến lược. Hãy đặt những câu hỏi thách thức sắc bén, phản biện các giả định ngầm định, giúp người dùng nhìn thấy rủi ro và nâng cao năng lực ra quyết định.',
    created_at: new Date().toISOString()
  }
];

export function VaultBotsPage() {
  const navigate = useNavigate();
  const { vaults } = useVaults();
  const { createConversation } = useChatHistory();

  const [bots, setBots] = useState<VaultBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Form state
  const [editingBot, setEditingBot] = useState<VaultBot | null>(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('🤖');
  const [role, setRole] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [targetVaultId, setTargetVaultId] = useState<string>('');

  const loadBots = async () => {
    setLoading(true);
    try {
      const res = await api.getVaultBots();
      const loaded: VaultBot[] = Array.isArray(res.bots) && res.bots.length > 0 ? res.bots : PRESET_BOTS;
      setBots(loaded);
    } catch (e) {
      console.error(e);
      setBots(PRESET_BOTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBots();
  }, []);

  const openCreateModal = () => {
    setEditingBot(null);
    setName('');
    setAvatar('🤖');
    setRole('');
    setDescription('');
    setSystemPrompt('');
    setTargetVaultId('');
    setShowModal(true);
  };

  const openEditModal = (b: VaultBot) => {
    setEditingBot(b);
    setName(b.name);
    setAvatar(b.avatar);
    setRole(b.role);
    setDescription(b.description);
    setSystemPrompt(b.system_prompt);
    setTargetVaultId(b.vault_id || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !systemPrompt.trim()) {
      alert('Vui lòng nhập Tên Bot và Chỉ dẫn System Prompt.');
      return;
    }

    const botObj: VaultBot = {
      id: editingBot ? editingBot.id : `bot_${Date.now()}`,
      name: name.trim(),
      avatar: avatar.trim() || '🤖',
      role: role.trim() || 'AI Assistant',
      description: description.trim(),
      system_prompt: systemPrompt.trim(),
      vault_id: targetVaultId || null,
      created_at: editingBot ? editingBot.created_at : new Date().toISOString()
    };

    let nextList: VaultBot[];
    if (editingBot) {
      nextList = bots.map(b => b.id === editingBot.id ? botObj : b);
    } else {
      nextList = [botObj, ...bots];
    }

    setBots(nextList);
    await api.saveVaultBots(nextList).catch(e => alert(e.message));
    setShowModal(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Xóa Bot AI này?')) return;
    const nextList = bots.filter(b => b.id !== id);
    setBots(nextList);
    await api.saveVaultBots(nextList).catch(e => alert(e.message));
  };

  const handleChatWithBot = (b: VaultBot) => {
    // Create new conversation with bot's persona injected into initial state
    const convId = createConversation([], `Hỏi đáp với ${b.name}`);
    // Save active bot persona in session or redirect
    localStorage.setItem('active_bot_persona', JSON.stringify(b));
    navigate('/chat');
  };

  const EMOJI_PICKER = ['🤖', '⚖️', '📊', '🛡️', '🦉', '🎯', '💼', '💡', '👨‍💻', '🚀'];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang tải Vault AI Bots...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">🤖 Vault AI Bots — Trợ Lý Persona Chuyên Biệt</h1>
          <p className="page-subtitle">
            Tạo và cá nhân hóa các AI chuyên gia vận hành trên kho tri thức riêng của bạn (Kho báu chỉ định hoặc toàn bộ Thư viện)
          </p>
        </div>
        <button className="btn btn--primary" onClick={openCreateModal}>
          + Tạo AI Bot Mới
        </button>
      </div>

      {/* Bot Cards Grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0 20px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {bots.map(bot => {
            const vaultName = vaults.find(v => v.id === bot.vault_id)?.name;
            return (
              <div
                key={bot.id}
                className="card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '14px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '14px',
                  padding: '20px',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '36px', background: 'rgba(255,255,255,0.05)', padding: '6px', borderRadius: '12px' }}>
                        {bot.avatar}
                      </span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>{bot.name}</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--accent-cyan)', fontWeight: 600 }}>{bot.role}</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn--ghost btn--sm" onClick={() => openEditModal(bot)} title="Sửa Bot">✏️</button>
                      <button className="btn btn--ghost btn--sm" onClick={() => handleDelete(bot.id)} title="Xóa Bot" style={{ color: '#f87171' }}>🗑</button>
                    </div>
                  </div>

                  <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '12px' }}>
                    {bot.description}
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span className="badge badge--purple" style={{ fontSize: '10.5px' }}>
                      🗄️ {vaultName ? `Kho báu: ${vaultName}` : 'Toàn bộ Kho tài liệu'}
                    </span>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px', display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn--primary btn--sm"
                    onClick={() => handleChatWithBot(bot)}
                    style={{ flex: 1, justifyContent: 'center', borderRadius: '8px' }}
                  >
                    💬 Bắt Đầu Hỏi Đáp
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" style={{ background: 'rgba(5, 5, 12, 0.85)', backdropFilter: 'blur(8px)', zIndex: 1000, position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content card" style={{ maxWidth: '540px', width: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700 }}>
                {editingBot ? '✏️ Cập Nhật AI Bot' : '➕ Tạo Mới AI Bot'}
              </h2>
              <button className="btn btn--ghost btn--sm" onClick={() => setShowModal(false)} style={{ fontSize: '16px' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* Avatar Picker & Name */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Avatar</label>
                  <select
                    className="input"
                    value={avatar}
                    onChange={e => setAvatar(e.target.value)}
                    style={{ fontSize: '20px', padding: '4px 8px', width: '65px' }}
                  >
                    {EMOJI_PICKER.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Tên Bot *</label>
                  <input
                    className="input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Ví dụ: Chuyên gia Soát xét Hợp đồng"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Chức danh / Chuyên môn *</label>
                <input
                  className="input"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  placeholder="Ví dụ: Senior Legal Counsel, Financial Analyst..."
                  style={{ width: '100%' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Mô tả ngắn gọn:</label>
                <input
                  className="input"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Bot này chuyên giúp bạn giải quyết việc gì?"
                  style={{ width: '100%' }}
                />
              </div>

              {/* Vault Selector */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Phạm vi Kho báu dữ liệu:</label>
                <select
                  className="input"
                  value={targetVaultId}
                  onChange={e => setTargetVaultId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">🌐 Tìm kiếm trên toàn bộ Thư viện</option>
                  {vaults.map(v => (
                    <option key={v.id} value={v.id}>📁 {v.name} ({v.type || 'business'})</option>
                  ))}
                </select>
              </div>

              {/* System Prompt */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Chỉ dẫn hệ thống (System Prompt Persona) *</label>
                <textarea
                  className="textarea"
                  rows={5}
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  placeholder="Định nghĩa phong cách, góc nhìn phân tích, tiêu chuẩn trả lời và những ranh giới cấm kỵ của Bot..."
                  style={{ width: '100%', fontSize: '13px', lineHeight: 1.6 }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button className="btn btn--secondary" onClick={() => setShowModal(false)}>Hủy</button>
                <button className="btn btn--primary" onClick={handleSave}>Lưu Bot</button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
